/**
 * Wrap tables in a horizontally-scrollable container on small screens.
 *
 * Usage: add `scroll-table` to your table:
 * <table class="basic-table scroll-table">...</table>
 */
let scrollTableIdCounter = 0;

export default function scrollTables () {
    const tables = document.querySelectorAll('table.basic-table.scroll-table');
    if (!tables.length) return;

    tables.forEach((table) => {
        let wrapper = table.closest('.scroll-table-wrap');

        // If not wrapped yet, wrap it.
        if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = 'scroll-table-wrap';

            // Accessibility: allow keyboard scrolling, announce region to AT
            wrapper.tabIndex = 0;
            wrapper.setAttribute('role', 'region');
            wrapper.setAttribute(
                'aria-label',
                table.getAttribute('aria-label') || table.getAttribute('data-scroll-label') || 'Scrollable table'
            );

            const parent = table.parentNode;
            if (!parent) return;

            parent.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        }

        // Ensure we run enhancements even if the wrapper already existed (e.g., cached DOM / partial reload).
        requestAnimationFrame(() => enhanceWrapper(wrapper));
    });
}

function enhanceWrapper (wrapper) {
    // Only enable enhancements when there is actual horizontal overflow.
    const isOverflowing = wrapper.scrollWidth > wrapper.clientWidth + 1;

    if (!isOverflowing) {
        wrapper.classList.remove('is-scrollable');
        return;
    }

    wrapper.classList.add('is-scrollable');
    ensureScrollButtons(wrapper);
    enableDragToScroll(wrapper);
    initScrollIndicators(wrapper);
}

function ensureScrollButtons (wrapper) {
    // Idempotent: only add once
    if (!wrapper.dataset.scrollTableId) {
        scrollTableIdCounter += 1;
        wrapper.dataset.scrollTableId = `st-${scrollTableIdCounter}`;
    }
    const id = wrapper.dataset.scrollTableId;

    // If controls already exist for this wrapper, don't add duplicates.
    if (wrapper.parentNode && wrapper.parentNode.querySelector(`.scroll-table-controls[data-scroll-table-id="${id}"]`)) {
        return;
    }

    const scrollByAmount = () => Math.max(160, Math.floor(wrapper.clientWidth * 0.75));

    const makeButton = (dir) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `scroll-table-arrow scroll-table-arrow--${dir}`;
        btn.setAttribute('aria-label', dir === 'left' ? 'Scroll table left' : 'Scroll table right');
        btn.innerHTML = `<span aria-hidden="true">${dir === 'left' ? '‹' : '›'}</span>`;
        btn.addEventListener('click', () => {
            wrapper.scrollBy({ left: dir === 'left' ? -scrollByAmount() : scrollByAmount(), behavior: 'smooth' });
        });
        return btn;
    };

    const makeControlsRow = (pos) => {
        const row = document.createElement('div');
        row.className = `scroll-table-controls scroll-table-controls--${pos}`;
        row.setAttribute('data-scroll-table-id', id);
        const left = makeButton('left');
        const right = makeButton('right');
        row.appendChild(left);
        row.appendChild(right);
        return { row, left, right };
    };

    const parent = wrapper.parentNode;
    if (!parent) return;

    const top = makeControlsRow('top');
    const bottom = makeControlsRow('bottom');

    parent.insertBefore(top.row, wrapper);
    parent.insertBefore(bottom.row, wrapper.nextSibling);

    // Store refs for state updates (disabled/hidden at ends).
    wrapper._scrollTableButtons = {
        left: [top.left, bottom.left],
        right: [top.right, bottom.right]
    };
}

function initScrollIndicators (wrapper) {
    if (wrapper.dataset.scrollIndicators === 'true') return;
    wrapper.dataset.scrollIndicators = 'true';

    const update = () => {
        const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;
        const atStart = wrapper.scrollLeft <= 1;
        const atEnd = wrapper.scrollLeft >= maxScrollLeft - 1;

        wrapper.classList.toggle('is-at-start', atStart);
        wrapper.classList.toggle('is-at-end', atEnd);
        wrapper.classList.toggle('has-scrolled', wrapper.scrollLeft > 2);

        const buttons = wrapper._scrollTableButtons;
        if (buttons) {
            buttons.left.forEach((btn) => btn.toggleAttribute('disabled', atStart));
            buttons.right.forEach((btn) => btn.toggleAttribute('disabled', atEnd));
        }
    };

    // Initial state + keep updated
    update();
    wrapper.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
}

function enableDragToScroll (wrapper) {
    if (wrapper.dataset.dragToScroll === 'true') return;
    wrapper.dataset.dragToScroll = 'true';

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId = null;

    const onPointerDown = (e) => {
        // Only enhance mouse dragging; touch/trackpad already scroll naturally.
        if (e.pointerType !== 'mouse') return;
        if (e.button !== 0) return;

        // Don't hijack clicks on interactive elements.
        const interactive = e.target && e.target.closest && e.target.closest('a, button, input, textarea, select, label');
        if (interactive) return;

        isDown = true;
        activePointerId = e.pointerId;
        startX = e.clientX;
        startScrollLeft = wrapper.scrollLeft;

        wrapper.classList.add('is-dragging');
        wrapper.setPointerCapture(activePointerId);
        e.preventDefault();
    };

    const onPointerMove = (e) => {
        if (!isDown) return;
        if (activePointerId !== e.pointerId) return;

        const dx = e.clientX - startX;
        wrapper.scrollLeft = startScrollLeft - dx;
        e.preventDefault();
    };

    const endDrag = () => {
        if (!isDown) return;
        isDown = false;
        activePointerId = null;
        wrapper.classList.remove('is-dragging');
    };

    wrapper.addEventListener('pointerdown', onPointerDown);
    wrapper.addEventListener('pointermove', onPointerMove);
    wrapper.addEventListener('pointerup', endDrag);
    wrapper.addEventListener('pointercancel', endDrag);
    wrapper.addEventListener('pointerleave', endDrag);
}


